// -didim_realty_world
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import TopInfoBar from "@/components/TopInfoBar";
import { useLanguage } from "@/contexts/LanguageContext";

interface MenuItem {
  label: string;
  path: string;
  children?: { label: string; path: string }[];
}

interface HeaderProps {
  layoutData?: any;
}

const Header: React.FC<HeaderProps> = ({ layoutData }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, setLanguage } = useLanguage();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [closeTimeout, setCloseTimeout] = useState<NodeJS.Timeout | null>(null);
  const location = useLocation();

  // layoutData'dan Header verilerini al
  const headerData = layoutData?.Header;
  const pageData = headerData?.[0];

  // Logo verisini al (title === 'logo' olan item)
  const logoData = headerData?.find((item: any) => item.title === 'logo');
  const logoUrl = logoData?.resim?.[0]?.url;

  const menuData = headerData?.filter((item: any) =>
    item.parent?.includes("6953b4c1ba458d6e153b3955")
  ) || [];

  const menuItems: MenuItem[] = menuData.map((item: any) => {
    const children = headerData?.filter((child: any) =>
      child.parent?.includes(item._id)
    ).map((child: any) => ({
      label: child.title,
      path: child.link || '#'
    }));
    return {
      label: item.title,
      path: item.link || '#',
      children: children && children.length > 0 ? children : undefined
    };
  });

  const handleMouseEnter = (label: string) => {
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      setCloseTimeout(null);
    }
    setActiveDropdown(label);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setActiveDropdown(null);
    }, 300); // 300ms delay before closing
    setCloseTimeout(timeout);
  };


  // const menuItems: MenuItem[] = [
  //   { label: 'Home', path: '/' },
  //   { label: 'Properties', path: '/properties' },
  //   {
  //     label: 'Area Guide',
  //     path: '/area-guide',
  //     children: [
  //       { label: 'Didim Area Guide', path: '/area-guide/didim' },
  //       { label: 'Akbuk Guide', path: '/area-guide/akbuk' },
  //       { label: 'Altinkum Guide', path: '/area-guide/altinkum' },
  //     ]
  //   },
  //   {
  //     label: 'Buyer Guide',
  //     path: '/buyer-guide',
  //     children: [
  //       { label: 'Your Budget', path: '/buyer-guide/your-budget' },
  //       { label: 'Choosing Your Property', path: '/buyer-guide/choosing-property' },
  //       { label: 'Purchase Procedure', path: '/buyer-guide/purchase-procedure' },
  //       { label: 'One-off Costs', path: '/buyer-guide/one-off-costs' },
  //       { label: 'Ongoing Costs', path: '/buyer-guide/ongoing-costs' },
  //       { label: 'Turkish Citizenship', path: '/buyer-guide/turkish-citizenship' },
  //     ]
  //   },
  //   { label: 'About Us', path: '/about' },
  //   {
  //     label: 'Contact',
  //     path: '/contact',
  //     children: [
  //       { label: 'Get in Touch', path: '/contact/get-in-touch' },
  //       { label: 'Meet the Team', path: '/contact/meet-the-team' },
  //     ]
  //   },
  // ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Top Info Bar */}
      <TopInfoBar
        phone={pageData?.tel || '+90 542 123 45 67'}
        email={pageData?.mail || 'info@didimrealty.com'}
        workingHours="Mon-Sat: 09:00 - 18:00"
      />

      {/* Sticky Navbar */}
      <nav className="sticky top-0 z-50 bg-white dark:bg-background-dark border-b border-gray-100 dark:border-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 flex items-center gap-2 cursor-pointer">
              {logoUrl ? (
                <div className="flex flex-col items-start">
                  <img
                    src={logoUrl}
                    alt="Didim Realty World Logo"
                    className="h-14 w-auto object-contain"
                  />
                  {logoData?.content && logoData.content !== '-' && (
                    <span className="text-xs text-gray-500 font-medium tracking-wide mt-0.5">
                      {logoData.content.replace(/<[^>]*>/g, '').trim()}
                    </span>
                  )}
                </div>
              ) : (
                <>
                  <div className="size-10 bg-primary rounded-lg flex items-center justify-center text-white">
                    <span className="material-symbols-outlined">home_work</span>
                  </div>
                  <div>
                    <h1 className="text-xl font-bold tracking-tight text-secondary dark:text-white leading-none">
                      Didim Realty
                    </h1>
                    <span className="text-xs text-gray-500 font-medium tracking-widest uppercase">World</span>
                  </div>
                </>
              )}
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              {menuItems.map((item) => (
                <div
                  key={item.path}
                  className="relative"
                  onMouseEnter={() => item.children && handleMouseEnter(item.label)}
                  onMouseLeave={handleMouseLeave}
                >
                  {item.children ? (
                    <>
                      <button
                        className={`text-sm font-medium flex items-center gap-1 transition-colors ${isActive(item.path) ? 'text-primary' : 'text-secondary dark:text-gray-200 hover:text-primary'
                          }`}
                      >
                        {item.label}
                        <span className="material-symbols-outlined text-[16px]">expand_more</span>
                      </button>
                      {activeDropdown === item.label && (
                        <div className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-100 dark:border-gray-700 py-2 z-50">
                          {item.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              className={`block px-4 py-2 text-sm transition-colors ${location.pathname === child.path
                                ? 'text-primary bg-primary/5'
                                : 'text-secondary dark:text-gray-200 hover:text-primary hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      className={`text-sm font-medium transition-colors ${isActive(item.path) ? 'text-primary' : 'text-secondary dark:text-gray-200 hover:text-primary'
                        }`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Right Actions */}
            <div className="hidden md:flex items-center gap-4">
              <button
                className={`text-sm font-bold transition-colors ${language === 'TR' ? 'text-primary' : 'text-gray-500 hover:text-primary'}`}
                onClick={() => setLanguage('TR')}
              >
                TR
              </button>
              <span className="text-gray-300">|</span>
              <button
                className={`text-sm font-bold transition-colors ${language === 'EN' ? 'text-primary' : 'text-gray-500 hover:text-primary'}`}
                onClick={() => setLanguage('EN')}
              >
                EN
              </button>
              {/* <Link
                to="/list-property"
                className="bg-primary hover:bg-red-700 text-white px-5 py-2.5 rounded-lg text-sm font-bold transition-all shadow-md shadow-primary/20"
              >
                List Property
              </Link> */}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center">
              <button
                className="text-secondary dark:text-white p-2"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <span className="material-symbols-outlined">
                  {isMobileMenuOpen ? 'close' : 'menu'}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white dark:bg-background-dark border-t border-gray-100 dark:border-gray-800">
            <div className="px-4 py-4 space-y-1">
              {menuItems.map((item) => (
                <div key={item.path}>
                  {item.children ? (
                    <>
                      <button
                        className={`flex items-center justify-between w-full text-sm font-medium py-2 transition-colors ${isActive(item.path) ? 'text-primary' : 'text-secondary dark:text-gray-200'
                          }`}
                        onClick={() => setActiveDropdown(activeDropdown === item.label ? null : item.label)}
                      >
                        {item.label}
                        <span className="material-symbols-outlined text-[16px]">
                          {activeDropdown === item.label ? 'expand_less' : 'expand_more'}
                        </span>
                      </button>
                      {activeDropdown === item.label && (
                        <div className="pl-4 space-y-1 border-l-2 border-primary/20 ml-2">
                          {item.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              className={`block text-sm py-2 transition-colors ${location.pathname === child.path
                                ? 'text-primary'
                                : 'text-gray-500 dark:text-gray-400 hover:text-primary'
                                }`}
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      className={`block text-sm font-medium py-2 transition-colors ${isActive(item.path) ? 'text-primary' : 'text-secondary dark:text-gray-200 hover:text-primary'
                        }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
              <div className="flex items-center gap-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                <button
                  className={`text-sm font-bold transition-colors ${language === 'TR' ? 'text-primary' : 'text-gray-500'}`}
                  onClick={() => setLanguage('TR')}
                >
                  TR
                </button>
                <span className="text-gray-300">|</span>
                <button
                  className={`text-sm font-bold transition-colors ${language === 'EN' ? 'text-primary' : 'text-gray-500'}`}
                  onClick={() => setLanguage('EN')}
                >
                  EN
                </button>
              </div>
              <Link
                to="/list-property"
                className="block w-full bg-primary hover:bg-red-700 text-white px-5 py-2.5 rounded-lg text-sm font-bold transition-all text-center mt-4"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                List Property
              </Link>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Header;
